import { formatElapsedRo } from '@/lib/format';
import { useActiveSession } from '@/lib/walletHooks';
import { useSessionDocked } from './sessionDrawerPresence';

/**
 * The docked face of `SessionDrawer` (2026-08-21 UI report: „cand este
 * minimizata, mutata in bara de sus").
 *
 * The app has no desktop top bar — `AppShell` is a left icon rail plus a
 * document canvas — so the nearest thing to „bara de sus" is `DocTop`, the
 * line that opens every page and already carries the system status dot. The
 * chip lands there, beside it.
 *
 * Renders only while the drawer is docked AND a session actually exists:
 * docking is about getting a live instance out of the way, not about leaving
 * a permanent empty slot in every page header. Clicking hands control back to
 * the corner card, which is where stopping the instance lives — this chip is
 * deliberately status-only, so a stray click on a page header can never
 * terminate a paid instance.
 */
export function SessionChip() {
  const [docked, setDocked] = useSessionDocked();
  const { data: active } = useActiveSession();

  if (!docked || !active) return null;

  const provisioning = active.provisioningState === 'provisioning';
  const label = provisioning ? 'Se pornește' : 'Instanță activă';

  return (
    <button
      type="button"
      onClick={() => setDocked(false)}
      title="Deschide panoul sesiunii"
      aria-label={`${label} — deschide panoul instanței`}
      className="inline-flex items-center gap-2 text-ink-3 hover:text-ink-2"
    >
      <span
        className={`size-2 rounded-full ${provisioning ? 'bg-warn motion-safe:animate-pulse' : 'bg-ok'}`}
        aria-hidden
      />
      <span className="text-[13.5px]">{label}</span>
      {!provisioning && (
        <span className="tabular-nums text-[13.5px] text-ink-3">· {formatElapsedRo(active.elapsedMs ?? 0)}</span>
      )}
    </button>
  );
}
